import React, { useEffect, useState, Suspense } from "react";
import ReactDOM from "react-dom";
import { HashRouter, Route, Switch, Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import Loading from "misc/Loading";
import AuthLayout from "layouts/Auth.js";
// Admin shell (desktop + mobile layouts and all admin views) is only needed
// once the user navigates into /admin. Loading it lazily keeps the heaviest
// part of the app out of the initial bundle so the first open — especially on
// mobile / slow connections — paints much faster.
const ResponsiveAdminLayout = React.lazy(() =>
  import("layouts/ResponsiveAdminLayout.js")
);
import config from "config";
import { useFetchData } from "apiService";
import logo from "assets/img/logo.png";
import { Center, VStack, Text, Box, Image, H1 } from "@chakra-ui/react";
import { ThemeModeProvider } from "context/ThemeContext";
import { ProfileProvider } from "context/ProfileContext";
import { SettingsProvider } from "context/SettingsContext";
import { SubscriptionProvider } from "context/SubscriptionContext";
import { ProfileAccessProvider } from "context/ProfileAccessContext";
import { BackendStatusProvider, useBackendStatus } from "context/BackendStatusContext";
import ErrorBoundary from "components/ErrorBoundary";
import FallbackPage from "components/FallbackPage";
import useMatomo from "hooks/useMatomo";
import "./i18n";
import "./Style.css";

// ==================== SUPPRESS BENIGN ResizeObserver ERROR ====================
// "ResizeObserver loop completed with undelivered notifications" (and the
// related "loop limit exceeded") is a harmless browser notice fired when layout
// work spans multiple animation frames. It has zero impact on functionality but
// triggers the dev error overlay. Swallow it so it doesn't interrupt the UI.
window.addEventListener("error", (e) => {
  if (
    e?.message === "ResizeObserver loop completed with undelivered notifications." ||
    e?.message === "ResizeObserver loop limit exceeded"
  ) {
    e.stopImmediatePropagation();
    e.preventDefault();
    const overlay = document.getElementById("webpack-dev-server-client-overlay");
    if (overlay) overlay.style.display = "none";
  }
});

// ==================== GLOBAL AXIOS CONFIG ====================
// Send credentials (cookies) for same-site + cross-site requests
axios.defaults.withCredentials = true;

// Attach Authorization header on every request (cross-origin fallback)
axios.interceptors.request.use((reqConfig) => {
  const token = Cookies.get("token");
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// Single global axios interceptor: clear stale session on 401
// (This is the ONLY 401 handler — BackendStatusContext handles connectivity only)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      if (Cookies.get("loggedIn")) {
        Cookies.remove("loggedIn");
        Cookies.remove("token");
        Cookies.remove("currentProfileId");
      }
    }
    return Promise.reject(error);
  }
);

// Loading screen timing constants
const MIN_LOADING_TIME = 1200; // Minimum 1.2 seconds of loading screen for smooth UX
const EXIT_ANIMATION_TIME = 500; // Exit animation duration

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  // Retrieved directly from the API when the app loads
  const { data: myRole } = useFetchData("myRole");
  const { data: myUser } = useFetchData("myUser");
  const history = useHistory();

  useEffect(() => {
    if (myRole) {
      const resolvedRole =
        (typeof myRole === "string" && myRole) ||
        myRole.role_id ||
        myRole.role ||
        "";
      config.setRole(resolvedRole);
    }
  }, [myRole]);

  useEffect(() => {
    if (myUser && myUser.email) {
      config.setEmail(myUser.email);
    }
  }, [myUser]);

  useEffect(() => {
    const startTime = Date.now();

    const checkToken = async () => {
      try {
        if (!Cookies.get("loggedIn") || window.location.hash.startsWith("#/auth")) {
          setIsAuthenticated(false);
        } else {
          // HttpOnly cookie is sent automatically via withCredentials
          const response = await axios.get(`${config.apiUrl}/checkToken`);

          if (response.status === 200) {
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        setIsAuthenticated(false);
      }

      // Ensure minimum loading time for smooth UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      setTimeout(() => {
        // Start exit animation + render app underneath
        setIsExiting(true);
        setIsLoading(false);
        
        // After exit animation completes, remove loading overlay
        setTimeout(() => {
          setShowLoading(false);
        }, EXIT_ANIMATION_TIME);
      }, remainingTime);
    };

    // Check token
    checkToken();

  }, []);

  return (
    <>
      {/* App renders underneath Loading when exit starts — so background is already painted */}
      {!isLoading && (
        <BackendStatusProvider>
          <HashRouter>
            <AppRoutes isAuthenticated={isAuthenticated} />
          </HashRouter>
        </BackendStatusProvider>
      )}
      {/* Loading overlay on top — fades out entirely revealing app underneath */}
      {showLoading && <Loading isExiting={isExiting} />}
    </>
  );
};

// Separate component for routes - checks backend status
const AppRoutes = ({ isAuthenticated }) => {
  useMatomo();
  const { isBackendAvailable, isChecking, hasCheckedOnce, retryConnection } = useBackendStatus();
  const [isRetrying, setIsRetrying] = React.useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    await retryConnection();
    setIsRetrying(false);
  };

  // Show loading while checking backend for the first time
  if (!hasCheckedOnce && isChecking) {
    return <Loading />;
  }

  // Show backend unavailable page if backend is down
  if (!isBackendAvailable) {
    return (
      <FallbackPage
        variant="connection"
        onRetry={handleRetry}
        isLoading={isRetrying || isChecking}
      />
    );
  }

  const isMobile = window.innerWidth < 768;
  const defaultAuth = isMobile ? "/auth/welcome" : "/auth/landing";

  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        {/* Routes d'authentification - toujours accessibles */}
        <Route path="/auth" component={AuthLayout} />
        
        {/* Routes admin - protégées */}
        <Route 
          path="/admin" 
          render={(props) => 
            isAuthenticated ? (
              <ResponsiveAdminLayout {...props} />
            ) : (
              <Redirect to={defaultAuth} />
            )
          } 
        />
        
        {/* Redirection racine */}
        <Route 
          exact 
          path="/" 
          render={() => (
              <Redirect to={isAuthenticated ? "/admin/dashboard" : defaultAuth} />
            )} 
          />
          
          {/* Fallback - redirection par défaut */}
          <Redirect to={isAuthenticated ? "/admin/dashboard" : defaultAuth} />
        </Switch>
    </Suspense>
  );
};

ReactDOM.render(
  <Suspense fallback={<div>Loading translations...</div>}>
    <ErrorBoundary>
      <ThemeModeProvider>
        <SettingsProvider>
          <SubscriptionProvider>
            <ProfileAccessProvider>
              <ProfileProvider>
                <App />
              </ProfileProvider>
            </ProfileAccessProvider>
          </SubscriptionProvider>
        </SettingsProvider>
      </ThemeModeProvider>
    </ErrorBoundary>
  </Suspense>,
  document.getElementById("root")
);


// TODO : Rajouter la licence ci-dessous dans la documentation ou je ne sais où.

/*
MIT License

Copyright (c) 2017 Creative Tim

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/